import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import GlassCard from "../../../components/common/GlassCard";

const RpmDeviceOrderingCard = ({
  rpmDeviceOrders = [],
  onAddRpmDeviceOrder,
  onDeleteRpmDeviceOrder,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const handleMenuClick = (event, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrder(null);
  };

  const handleDelete = () => {
    if (selectedOrder && onDeleteRpmDeviceOrder) {
      onDeleteRpmDeviceOrder(selectedOrder.id);
    }
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    const statusColors = {
      ordered: "info",
      shipped: "warning",
      delivered: "success",
      activated: "success",
      pending: "default",
      cancelled: "error",
    };
    return statusColors[status?.toLowerCase()] || "default";
  };

  return (
    <GlassCard
      title="RPM Device Orders"
      addLabel="Add RPM Device Order"
      onAdd={onAddRpmDeviceOrder}
    >
      {rpmDeviceOrders && rpmDeviceOrders.length > 0 ? (
        <List dense>
          {rpmDeviceOrders.map((order, index) => (
            <ListItem
              key={order.id || index}
              sx={{
                borderRadius: 1,
                mb: 1,
                bgcolor: "rgba(255, 255, 255, 0.02)",
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.05)",
                },
              }}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="Order options"
                  size="small"
                  onClick={(e) => handleMenuClick(e, order)}
                  data-action="open-rpm-device-order-menu"
                  data-category="rpm-device-order"
                  data-order-id={order.id}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {order.device_type || "Unknown Device"}
                    </Typography>
                    <Chip
                      label={order.status || "Pending"}
                      size="small"
                      color={getStatusColor(order.status)}
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {order.manufacturer && `${order.manufacturer} • `}
                      {order.model && `${order.model}`}
                    </Typography>
                    {order.order_date && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Ordered: {new Date(order.order_date).toLocaleDateString()}
                      </Typography>
                    )}
                    {order.delivery_date && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Delivered: {new Date(order.delivery_date).toLocaleDateString()}
                      </Typography>
                    )}
                    {order.tracking_number && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Tracking: {order.tracking_number}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 2 }}>
          No RPM device orders
        </Typography>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        data-category="rpm-device-order-menu"
      >
        <MenuItem
          onClick={handleDelete}
          data-action="delete-rpm-device-order"
          data-category="rpm-device-order"
        >
          Delete Order
        </MenuItem>
      </Menu>
    </GlassCard>
  );
};

RpmDeviceOrderingCard.propTypes = {
  rpmDeviceOrders: PropTypes.array,
  onAddRpmDeviceOrder: PropTypes.func,
  onDeleteRpmDeviceOrder: PropTypes.func,
};

export default RpmDeviceOrderingCard;
